import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore, Visibility, Psychology } from '@mui/icons-material';
import {
  AIR_HUNGER_IS_NOT_AN_OXYGEN_GAUGE,
  CONTRACTIONS_ARE_NOT_A_WARNING,
  NEAR_BLACKOUT_SIGNS,
  NEAR_BLACKOUT_SYMPTOMS,
  POST_EVENT_RULES,
  POST_SURFACE_RISK_NOTE,
} from '../../data/blackoutSigns';
import {
  BOLT_SCALE,
  PEAK_INHALATION_CAUTION,
  PEAK_INHALATION_SEGMENTS,
  POST_HOLD_MONITOR_SECONDS,
  PURGE_BREATHING,
  RECOVERY_DEPTH,
  RECOVERY_STATIC,
  REQUIRED_RECOVERY_BREATHS,
  VENTILATION_BREATHING,
  VENTILATION_EXHALE_RANGE_SECONDS,
} from '../../data/apneaProtocols';
import { BOLT_DECLINE_DAYS } from '../../utils/apneaRules';

/** Step-by-step BOLT procedure. Each step is one deliberate action, in order. */
const BOLT_STEPS: Array<{ title: string; detail: string }> = [
  {
    title: 'Time it right',
    detail: 'On waking, before you get out of bed, check your phone, or have coffee. '
      + 'Caffeine, movement and stress all raise CO₂ tolerance temporarily and inflate '
      + 'the number.',
  },
  {
    title: 'Sit or lie still for a minute',
    detail: 'Settle into a relaxed position and breathe normally through your nose for '
      + 'about a minute, until your breathing feels steady and unforced.',
  },
  {
    title: 'Take a normal breath in, then a normal breath out',
    detail: 'Not a deep breath, not a full exhale — just an ordinary, comfortable breath '
      + 'cycle, the same size as the ones before it. Over-inhaling or over-exhaling is '
      + 'the single most common way this test gets invalidated.',
  },
  {
    title: 'Pinch your nose and start the timer',
    detail: 'Immediately after that normal exhale, pinch your nose closed (or hold the '
      + 'breath at the throat) and start timing at the same instant.',
  },
  {
    title: 'Stop at the FIRST definite urge to breathe',
    detail: 'Not the first flicker of awareness that you are holding your breath — the '
      + 'first clear, unmistakable signal from your body that it wants to breathe. This '
      + 'is often a small involuntary movement in the diaphragm or throat. Stop the timer '
      + 'there, release your nose, and breathe normally.',
  },
  {
    title: 'Check your recovery breath',
    detail: 'If your first breath after stopping is large, fast, or gasping, you held '
      + 'past the real signal and the number is invalid — the recovery breath should '
      + 'feel unremarkable, just a normal inhale. Rest a few minutes and repeat if it '
      + "wasn't.",
  },
];

const BOLT_MISTAKES = [
  'Testing after coffee, exercise, or getting out of bed — all falsely raise the score.',
  'Taking a bigger-than-normal breath in before pinching the nose.',
  'Holding past the first urge to "see how far you can go" — that turns it into a max '
    + 'breath-hold, which measures something different and invites unnecessary risk.',
  'Testing while anxious, sick, or sleep-deprived, then comparing that score to a '
    + 'well-rested baseline.',
];

/** Step-by-step max-static procedure. A supervised, single-attempt measurement. */
const MAX_STATIC_STEPS: Array<{ title: string; detail: string }> = [
  {
    title: 'Never do this alone',
    detail: 'A max attempt is pushed past the first urge to breathe, which is exactly the '
      + 'territory where hypoxia and blackout live. Do this only with a rescue-trained '
      + 'buddy present, watching you the whole time — not in another room, not "checking in".',
  },
  {
    title: 'Rest and breathe up first',
    detail: 'At least a few minutes of normal breathing beforehand, plus the ventilation '
      + 'breathing pattern from this app (quick in, long out) for a minute or two to settle '
      + 'your heart rate. Do not hyperventilate — it delays the urge to breathe without '
      + 'delaying hypoxia, which removes your warning system without removing the danger.',
  },
  {
    title: 'One comfortable inhale, then hold',
    detail: 'A full but comfortable breath in — not a forced, maximal one. Your buddy starts '
      + 'timing the moment you go still.',
  },
  {
    title: 'Ride out the contractions',
    detail: 'Diaphragmatic contractions will start well before any real danger — they are a '
      + 'CO₂ signal, not a warning sign. Relax into them rather than fighting them.',
  },
  {
    title: 'Stop at the first actual warning sign — not at your limit',
    detail: 'This is not a hold-until-you-must-breathe test. End it the moment you notice '
      + 'any of the warning signs, or sooner if anything feels wrong. A false "personal best" '
      + 'is worthless next to the risk of pushing further.',
  },
  {
    title: 'Recovery breathe immediately',
    detail: `${REQUIRED_RECOVERY_BREATHS} recovery breaths the instant you stop, then stay `
      + `upright and keep breathing for at least ${POST_HOLD_MONITOR_SECONDS} seconds — most `
      + `blackouts happen after surfacing, not during the hold.`,
  },
  {
    title: 'Log it and stop for the day',
    detail: 'Record the time under Today or in plan setup. One max attempt is plenty for a '
      + 'session — this is a measurement, not a training set, and repeating it does not '
      + 'improve anything.',
  },
];

/**
 * Reference content, straight from the training sources.
 *
 * The recovery-breathing and hypoxia sections lead, because they are the two the
 * diver most needs before a session rather than after it. The signs/symptoms split
 * is preserved: for solo dry training only the symptoms column is self-observable,
 * and merging them would imply otherwise.
 */

/**
 * Anchor ids for sections other pages link to directly (e.g. Plan setup's BOLT and
 * max-static fields). Exported so a link can be built without hard-coding a string
 * that would silently break if this file's markup changes.
 */
export const LEARN_ANCHORS = {
  boltTest: 'learn-bolt-test',
  maxStaticTest: 'learn-max-static-test',
  recoveryBreathing: 'learn-recovery-breathing',
  contractions: 'learn-contractions',
  recognisingHypoxia: 'learn-recognising-hypoxia',
  peakInhalation: 'learn-peak-inhalation',
  overtrainingSignals: 'learn-overtraining-signals',
} as const;

const LearnReference: React.FC = () => (
  <Stack spacing={2}>
    {/* Recovery breathing first: NAUI requires it after every hold, no exceptions. */}
    <Paper id={LEARN_ANCHORS.recoveryBreathing} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>Recovery breathing</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {REQUIRED_RECOVERY_BREATHS} breaths after <strong>every</strong> breath-hold, no
        exceptions. These are the most important breaths you will take in preventing
        blackout.
      </Alert>

      <Typography variant="subtitle2">{RECOVERY_STATIC.name}</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {RECOVERY_STATIC.appliesTo}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Six rapid cleansing breaths — one second in, one second out. These exchange the
        CO₂-laden air in your lungs for oxygen-rich air.
      </Typography>

      <Typography variant="subtitle2">{RECOVERY_DEPTH.name}</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {RECOVERY_DEPTH.appliesTo} — out of scope for dry training, included for completeness.
      </Typography>
      <Typography variant="body2">
        Three hook breaths (full inhale of the top half of the lungs, held three seconds, to
        raise blood pressure) then three cleansing breaths.
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Alert severity="warning">
        {POST_SURFACE_RISK_NOTE} Stay upright and keep breathing for at least{' '}
        {POST_HOLD_MONITOR_SECONDS} seconds — do not close your eyes or lie back until
        breathing is fully normal.
      </Alert>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {RECOVERY_STATIC.citation}
      </Typography>
    </Paper>

    {/* BOLT test walkthrough. Central to plan setup and the daily Today entry, so it
        gets a full step-by-step rather than the terse one-liner shown inline there. */}
    <Paper id={LEARN_ANCHORS.boltTest} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>The BOLT test, step by step</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        BOLT (Body Oxygen Level Test) is a control-pause measurement, not a breath-hold
        contest. It estimates how sensitive your body is to rising CO₂ — the real
        trigger behind the urge to breathe — and it is only useful if measured the same
        careful way every time.
      </Alert>

      <List dense disablePadding>
        {BOLT_STEPS.map((step, i) => (
          <ListItem key={step.title} disableGutters sx={{ alignItems: 'flex-start', py: 1 }}>
            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
              <Chip
                label={i + 1}
                size="small"
                color="primary"
                sx={{ height: 22, minWidth: 22, '& .MuiChip-label': { px: 1 } }}
              />
            </ListItemIcon>
            <ListItemText
              primary={step.title}
              secondary={step.detail}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>What the score means</Typography>
      <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        {BOLT_SCALE.map(band => (
          <Chip
            key={band.label}
            size="small"
            variant="outlined"
            label={
              band.maxSeconds === null
                ? `${band.label} — 40s+`
                : `${band.label} — up to ${band.maxSeconds}s`
            }
          />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Below 10 seconds suggests breathing pattern issues worth addressing before
        breath-hold training at all. 20 seconds and up is a reasonable floor to begin
        training from; consistently below that, favour breathing-pattern work over
        longer holds.
      </Typography>

      <Alert severity="warning">
        Common ways this test gets invalidated:
        <List dense disablePadding sx={{ mt: 0.5 }}>
          {BOLT_MISTAKES.map(m => (
            <ListItem key={m} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
              <ListItemText primary={m} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Alert>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Measure daily, at the same time and under the same conditions, and track the
        trend rather than any single reading — day-to-day noise of a few seconds is
        normal. A steady decline over several days is the signal that matters, not one
        low morning.
      </Typography>
    </Paper>

    {/* Max-static walkthrough. This is the riskier of the two baseline measurements
        plan setup asks for, so it leans on the abort triggers and hypoxia sections
        below rather than repeating them, and puts the buddy requirement first. */}
    <Paper id={LEARN_ANCHORS.maxStaticTest} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>The max-static test, step by step</Typography>
      <Alert severity="error" sx={{ mb: 2 }}>
        This is a supervised measurement, not a training exercise. Unlike BOLT, it is
        deliberately pushed past the first urge to breathe — the exact zone where
        near-blackout and blackout happen. Skip it entirely if you do not have a
        rescue-trained buddy available; a BOLT-based estimate is a safe substitute for
        plan setup.
      </Alert>

      <List dense disablePadding>
        {MAX_STATIC_STEPS.map((step, i) => (
          <ListItem key={step.title} disableGutters sx={{ alignItems: 'flex-start', py: 1 }}>
            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
              <Chip
                label={i + 1}
                size="small"
                color="primary"
                sx={{ height: 22, minWidth: 22, '& .MuiChip-label': { px: 1 } }}
              />
            </ListItemIcon>
            <ListItemText
              primary={step.title}
              secondary={step.detail}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Review the warning signs and abort criteria below before attempting this, and
        again the abort triggers shown mid-session. Knowing them cold before you start
        matters more here than during ordinary table training, since a max attempt
        gives you less margin.
      </Typography>
    </Paper>

    {/* Contractions. Shown live in the hold screen and referenced in the check-in
        and log, so this is the one place that spells out what they are. */}
    <Paper id={LEARN_ANCHORS.contractions} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>Contractions</Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {CONTRACTIONS_ARE_NOT_A_WARNING}
      </Alert>

      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Diaphragmatic contractions are your diaphragm involuntarily twitching as CO₂
        builds up — usually felt as a jerk or flutter around the midsection. They are
        the same mechanism as hiccups: an automatic muscle response, not something you
        are doing wrong or a signal that anything dangerous is happening.
      </Typography>

      <Typography variant="body2" sx={{ mb: 1.5 }}>
        They typically start well before any real risk and, for most people, become
        the dominant sensation of a hold long before oxygen runs low. Training makes
        them more tolerable, not less frequent — the same contraction load feels
        easier as you relax into it, which is the actual skill "Train" is building.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The session runner asks you to mark the first one during a hold, and later
        screens (check-in, session log) show that time back to you. It exists purely
        as a training data point — a later first-contraction time across sessions is
        a sign of improving CO₂ tolerance — not as a countdown to danger.
      </Typography>

      <Alert severity="warning">
        Contractions are not a stopping signal on their own. The{' '}
        <Link component={RouterLink} to={`?tab=learn#${LEARN_ANCHORS.recognisingHypoxia}`}>
          actual warning signs
        </Link>{' '}
        below are — tunnel vision, tingling, dizziness, disorientation, or any
        involuntary twitching outside the diaphragm.
      </Alert>
    </Paper>

    {/* Hypoxia recognition. */}
    <Paper id={LEARN_ANCHORS.recognisingHypoxia} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>Recognising hypoxia</Typography>

      <Alert severity="error" sx={{ mb: 2 }}>
        {AIR_HUNGER_IS_NOT_AN_OXYGEN_GAUGE}
      </Alert>
      <Alert severity="info" sx={{ mb: 2 }}>
        {CONTRACTIONS_ARE_NOT_A_WARNING}
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Psychology fontSize="small" color="primary" />
            <Typography variant="subtitle2">Symptoms — what you feel</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Training dry and alone, these are the only ones you can detect yourself.
          </Typography>
          <List dense>
            {NEAR_BLACKOUT_SYMPTOMS.map(s => (
              <ListItem key={s.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={s.label}
                  secondary={s.description}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Visibility fontSize="small" color="primary" />
            <Typography variant="subtitle2">Signs — what a buddy sees</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            What a trained buddy watches for. You cannot observe these in yourself.
          </Typography>
          <List dense>
            {NEAR_BLACKOUT_SIGNS.map(s => (
              <ListItem key={s.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={s.label}
                  secondary={s.description}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>After an event</Typography>
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemText
            primary={POST_EVENT_RULES.nauiSameDayStop.rule}
            secondary={POST_EVENT_RULES.nauiSameDayStop.citation}
            primaryTypographyProps={{ variant: 'body2' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={POST_EVENT_RULES.conservativeTablePause.rule}
            secondary={POST_EVENT_RULES.conservativeTablePause.citation}
            primaryTypographyProps={{ variant: 'body2' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemText
            primary={POST_EVENT_RULES.repeatEvents.rule}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>
      <Alert severity="warning" sx={{ mt: 1 }}>
        Rescue technique is not covered here. It needs hands-on instruction — this app is
        dry-land training only.
      </Alert>
    </Paper>

    {/* Breathing patterns. */}
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>Breathing patterns</Typography>

      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
          <Typography variant="subtitle2">{VENTILATION_BREATHING.name}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Typography variant="body2" gutterBottom>{VENTILATION_BREATHING.purpose}</Typography>
          <Stack direction="row" spacing={0.5} sx={{ my: 1, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label="inhale 2s" />
            <Chip size="small" label="hold 2s" />
            <Chip
              size="small"
              label={`exhale ${VENTILATION_EXHALE_RANGE_SECONDS.min}-${VENTILATION_EXHALE_RANGE_SECONDS.max}s`}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {VENTILATION_BREATHING.caution}
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion
        id={LEARN_ANCHORS.peakInhalation}
        disableGutters
        elevation={0}
        sx={{ '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
          <Typography variant="subtitle2">Peak inhalation</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            The last breath before a hold, taken in the order the lungs actually fill —
            bottom to top — so no space is left unused and no muscle is fighting another
            for room. Each segment is a body cue to chase, not a timed step; move to the
            next once you feel that area has filled.
          </Typography>
          <List dense disablePadding>
            {PEAK_INHALATION_SEGMENTS.map((seg, i) => (
              <ListItem key={seg.segment} disableGutters>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Typography variant="body2" color="text.secondary">{i + 1}.</Typography>
                </ListItemIcon>
                <ListItemText
                  primary={seg.segment}
                  secondary={seg.cue}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
          <Alert severity="warning" sx={{ mt: 1 }}>{PEAK_INHALATION_CAUTION}</Alert>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            A full peak inhalation raises chest pressure sharply, which can briefly
            reduce blood flow to the brain right as you go still — the opposite of when
            you want to feel light-headed. Build up to the full sequence over sessions
            rather than taking it to 100% from the first attempt.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0 }}>
          <Typography variant="subtitle2">{PURGE_BREATHING.name}</Typography>
          <Chip size="small" label="optional" sx={{ ml: 1 }} variant="outlined" />
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Typography variant="body2" gutterBottom>{PURGE_BREATHING.purpose}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            1 second in, 4 seconds out. {PURGE_BREATHING.repetitions!.min}–
            {PURGE_BREATHING.repetitions!.max} breaths maximum.
          </Typography>
          <Alert severity="warning">{PURGE_BREATHING.caution}</Alert>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Off by default in this app. Never use purging to push a hold past your planned
            limit, and never hyperventilate.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Paper>

    {/* Overtraining signals. The Log tab's decline chip and the check-in's
        "progression gate" both act on this rule without explaining it in place —
        this is where that explanation lives. */}
    <Paper id={LEARN_ANCHORS.overtrainingSignals} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle1" gutterBottom>Overtraining signals</Typography>

      <Typography variant="body2" sx={{ mb: 1.5 }}>
        BOLT is measured daily specifically because it is the earliest available signal
        of accumulated fatigue — it responds to poor sleep, stress, illness and
        under-recovery well before performance drops in a session. A single low reading
        is normal noise; a run of falling readings is not.
      </Typography>

      <Alert severity="warning" sx={{ mb: 1.5 }}>
        {BOLT_DECLINE_DAYS} consecutive days of falling BOLT is treated as the earliest
        overtraining warning available. When you see it — on the Log tab, or the "down
        {' '}{BOLT_DECLINE_DAYS} readings" chip on the trend chart — take two easy days
        before your next hard session rather than pushing through it.
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The Plan check-in uses a related but separate signal: your self-rated recovery
        score after each session, trended over the check-in window. A declining or
        low recovery trend is what actually gates progressing to a harder phase — it is
        called the <strong>progression gate</strong> for that reason, and the check-in
        always shows its reasoning rather than a bare "not yet".
      </Typography>

      <Typography variant="caption" color="text.secondary">
        Both signals are conservative by design: false alarms cost a couple of easy
        days, while training through a real decline risks a session going worse than
        planned.
      </Typography>
    </Paper>
  </Stack>
);

export default LearnReference;
